/**
 * "12 minutes ago", in the account's own language.
 *
 * The Overview's status line is a claim about RIGHT NOW, so the one number on it that
 * has to be alive is when the assistant last said anything. An ISO timestamp is not that
 * claim — the owner would have to do the subtraction herself, in UTC.
 *
 * `Intl.RelativeTimeFormat` does the wording and the plural agreement, which is the part
 * that cannot be hand-rolled: Arabic has six plural forms and "منذ دقيقتين" is a dual,
 * not "منذ 2 دقيقة". So the unit choice is ours and every word is the platform's.
 *
 * The locale comes through `toIntlLocale`, which is where Chatwoot's non-BCP-47
 * directory names (`pt_BR`) are turned into tags `Intl` will accept — constructing one
 * directly throws a RangeError, and a throw inside a `computed` during render blanks the
 * whole section.
 */

import { toIntlLocale } from './intl.js';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * @param {string} iso An ISO instant, or anything at all — a value that does not parse
 *   returns null rather than "Invalid Date ago".
 * @param {string} locale vue-i18n's locale (Chatwoot's directory name is fine).
 * @param {{ now?: number, justNow?: string }} [options] `justNow` is the caller's own
 *   translated string for "less than a minute"; `RelativeTimeFormat` would otherwise say
 *   "in 0 seconds" for a reply that has just landed.
 * @returns {?string}
 */
export const relativeTime = (iso, locale, options = {}) => {
  if (typeof iso !== 'string' || iso === '') return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const { now = Date.now(), justNow = null } = options;
  // A clock skew between the browser and the server can put "last reply" a few seconds
  // in the future. Clamping to 0 turns that into "just now" instead of "in 3 seconds",
  // which would read as a scheduled reply that has not happened.
  const elapsed = Math.max(0, now - at.getTime());

  if (elapsed < MINUTE) return justNow;

  let format;
  try {
    format = new Intl.RelativeTimeFormat(toIntlLocale(locale), {
      numeric: 'auto',
    });
  } catch {
    return null;
  }

  if (elapsed < HOUR)
    return format.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return format.format(-Math.floor(elapsed / HOUR), 'hour');
  return format.format(-Math.floor(elapsed / DAY), 'day');
};

export default relativeTime;
